import { useState, useEffect } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { BaseSelectedOptionsProps } from './MultiSelectDropdown.types';
import { formatResponse, getJourneyResultsFromData } from './MultiSelectDropdown.utils';
import { WorkflowType } from 'modules/domains';

const FETCH_POLICY = 'cache-and-network';

export const GET_JOURNEYS = gql`
query getWorkflows($search: String, $filterInfo: WorkflowFilterInfoInput) {
  workflows(search: $search, filterInfo: $filterInfo) {
    results {
        id
        name
    }
  }
}`;

export const GET_JOURNEY = gql`
    query getWorkflow($id: Long!) {
        workflow(id: $id) {
            id
            name
        }   
    }
`;

type ResultItem = {
  id: number;
  name: string;
};

export const useGetJourneyMetadataSelectOptions = ({
  defaultOptions,
  values = [],
}: BaseSelectedOptionsProps) => {
  const [options, setOptions] = useState(defaultOptions);
  const [hasError, setHasError] = useState(false);

  const [selectedJourneys, setSelectedJourneys] = useState({});
  const [journeys, setNonselectedJourneys] = useState<any[]>([]);
  const [fetchQueue, setFetchQueue] = useState<any[]>([]);

  /* -------------------- order and set functions --------------------*/
  const formatAndSetOptions = (results: ResultItem[]) => {
    const formattedOptions = formatResponse(results);
    setOptions(formattedOptions);
  };

  const fetchAndReorderOptions = (results: ResultItem[]) => {
    const journeyResults: ResultItem[] = [...results];
    const nonFetched: number[] = [];
    let fetched = {};

    values.forEach((item) => {
      const journeyId = Number(item);
      const idx = journeyResults.findIndex((opt) => opt.id === journeyId);

      if (idx > -1) {
        const selected = journeyResults.splice(idx, 1);
        fetched = {
          ...fetched,
          [journeyId]: selected[0],
        };
      } else {
        nonFetched.push(journeyId);
      }
    });

    // use journeys data if we already have it
    setSelectedJourneys(fetched);
    // fetch journeys that aren't in the initial query
    setFetchQueue(nonFetched);
    // store everything else
    setNonselectedJourneys(journeyResults);
  };

  const updateFetchedJourneys = (data: Record<number, any>) => {
    setSelectedJourneys((addtlJourneys) => ({
      ...addtlJourneys,
      ...data,
    }));
    setFetchQueue((fq) => fq.slice(1));
  };

  /* -------------------- queries --------------------*/
  const [getJourney] = useLazyQuery(GET_JOURNEY, {
    fetchPolicy: FETCH_POLICY,
    onError: () => {
      // we should still display the rest of the journeys if this fails
      updateFetchedJourneys({
        [fetchQueue[0]]: false,
      });
      setHasError(true);
    },
    onCompleted: (data) => {
      updateFetchedJourneys({
        [data?.workflow?.id]: data?.workflow,
      });
      setHasError(false);
    },
  });

  const [getJourneys, { loading: journeysLoading, variables }] = useLazyQuery(GET_JOURNEYS, {
    fetchPolicy: FETCH_POLICY,
    onError: () => setHasError(true),
    variables: {
      search: '',
      filterInfo: {
        workflowType: WorkflowType.PUBLISHED,
        workflowState: ['Enabled'],
      },
    },
    onCompleted: (data) => {
      const results = getJourneyResultsFromData(data);
      if (variables?.search || !values.length) {
        formatAndSetOptions(results);
      } else {
        fetchAndReorderOptions(results);
      }
      setHasError(false);
    },
  });

  /* ---------- useEffects for handling single fetched journeys ---------- */
  useEffect(() => {
    const addtlJourneys = Object.values(selectedJourneys);
    if (values.length && addtlJourneys.length === values.length) {
      formatAndSetOptions([...addtlJourneys.filter((item) => !!item).reverse(), ...journeys]);
    }
  }, [selectedJourneys, journeys]);

  useEffect(() => {
    if (fetchQueue.length) {
      getJourney({
        variables: { id: fetchQueue[0] },
      });
    }
  }, [fetchQueue]);

  /* -------------------- configuration -------------------- */
  const runQuery = (opts: any) => {
    if (GET_JOURNEYS) {
      getJourneys(opts);
    }
  };

  return {
    hasError,
    options,
    runQuery,
    isLoading: journeysLoading,
    fieldName: 'Journeys',
  };
};
